import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import type { MaxsoldBidsDto, MaxsoldLotDto, MaxsoldStatusDto } from '@jordan/types';
import { Alert, Breadcrumb, Button, DataTable, PageHeader, Spinner } from '@jordan/ui';
import { AppChrome } from '../../components/AppChrome';
import { ApiError, apiGet } from '../../lib/api';
import { maxsoldSidebar } from './maxsoldSidebar';
import { useWatchlist } from './watchlist';
import { ListingGrid, listingColumns, type ListingView } from './MaxsoldListing';

/** Won/Lost (closed) or Winning/Outbid (live) badge for a bid lot. */
function bidBadge(f: MaxsoldLotDto, now: number): ListingView['badge'] {
  const ended = f.endTime ? Date.parse(f.endTime) < now : false;
  if (ended) return f.isWinning ? { label: 'Won', variant: 'success' } : { label: 'Lost', variant: 'secondary' };
  return f.isWinning ? { label: 'Winning', variant: 'success' } : { label: 'Outbid', variant: 'danger' };
}

export function MaxsoldBidsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [status, setStatus] = useState<MaxsoldStatusDto | null>(null);
  const [data, setData] = useState<MaxsoldBidsDto | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [view, setView] = useState<'grid' | 'list'>('grid');
  // hideclosed toggle: true = only lots still active; false = all bids (incl. closed).
  // Initialised from the configured default once status loads (the toggle isn't shown before then).
  const [activeOnly, setActiveOnly] = useState(false);
  const wl = useWatchlist();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    apiGet<MaxsoldStatusDto>('/api/maxsold/status')
      .then((s) => {
        setStatus(s);
        setActiveOnly(s.bidsActiveOnlyDefault);
      })
      .catch(() => setErr('Failed to load Maxsold status.'));
  }, []);

  const load = useCallback((activeOnlyFlag: boolean) => {
    setLoading(true);
    setErr(null);
    apiGet<MaxsoldBidsDto>(`/api/maxsold/bids?hideclosed=${activeOnlyFlag ? 1 : 0}`)
      .then(setData)
      .catch((e: unknown) => {
        const body = e instanceof ApiError ? (e.body as { message?: string } | null) : null;
        setErr(body?.message ?? 'Could not load your bids.');
        setData(null);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (status?.configured) load(activeOnly);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status?.configured, activeOnly]);

  const openLot = useCallback((id: number) => navigate(`/maxsold/lot/${id}?from=bids`), [navigate]);

  const kw = query.trim().toLowerCase();
  const views = useMemo<ListingView[]>(
    () =>
      (data?.items ?? [])
        .filter(
          (f) => kw === '' || `${f.title} ${f.auctionTitle ?? ''} ${f.lotNumber ?? ''}`.toLowerCase().includes(kw),
        )
        .map((f) => ({
          id: f.id,
          imageUrl: f.imageUrl,
          title: f.title,
          subtitle: `${f.auctionTitle ?? `Auction ${f.auctionId}`}${f.lotNumber ? ` · Lot ${f.lotNumber}` : ''}`,
          badge: bidBadge(f, now),
          price: f.currentBid,
          secondaryPrice: f.proxyBid,
          bidCount: f.bidCount,
          closeTime: f.endTime,
          favourite: wl.configured
            ? { active: wl.isFavourite(f.id), pending: wl.isPending(f.id), onToggle: () => wl.toggle(f.id) }
            : null,
        })),
    [data, kw, now, wl],
  );

  const columns = useMemo(
    () => listingColumns({ now, price: true, secondaryPrice: true, bids: true, status: true, favourite: wl.configured }),
    [now, wl.configured],
  );

  return (
    <AppChrome secondarySidebar={maxsoldSidebar(navigate, pathname)}>
      <PageHeader
        title={
          <>
            Maxsold - <span className="fw-normal">Bids</span>
          </>
        }
        breadcrumb={<Breadcrumb items={[{ icon: 'ph-house', href: '/' }, { label: 'Maxsold' }, { label: 'Bids' }]} />}
      />

      <div className="content">
        {status && !status.configured && (
          <Alert variant="info" icon="ph-info">
            Maxsold isn&apos;t connected. Add <code>MAXSOLD_EMAIL</code> and <code>MAXSOLD_PASSWORD</code> to the server{' '}
            <code>.env</code> to view your bids.
          </Alert>
        )}
        {err && (
          <Alert variant="danger" icon="ph-warning" dismissible>
            {err}
          </Alert>
        )}

        {status?.configured && (
          <div className="card">
            <div className="card-header d-flex flex-wrap align-items-center gap-2">
              <h5 className="mb-0 me-auto">Bids{data ? <span className="text-muted fw-normal ms-2">{data.total} lots</span> : null}</h5>
              <div className="form-check form-switch mb-0">
                <input
                  className="form-check-input"
                  type="checkbox"
                  role="switch"
                  id="maxsold-active-only"
                  checked={activeOnly}
                  disabled={loading}
                  onChange={(e) => setActiveOnly(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="maxsold-active-only">
                  Active lots only
                </label>
              </div>
              <div className="form-control-feedback form-control-feedback-start" style={{ maxWidth: 240 }}>
                <input
                  type="search"
                  className="form-control"
                  placeholder="Search…"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                />
                <div className="form-control-feedback-icon">
                  <i className="ph-magnifying-glass opacity-50" />
                </div>
              </div>
              <div className="btn-group" role="group" aria-label="View">
                {(['grid', 'list'] as const).map((v) => (
                  <button
                    key={v}
                    type="button"
                    className={`btn btn-sm ${view === v ? 'btn-primary' : 'btn-light'}`}
                    onClick={() => setView(v)}
                    title={v}
                  >
                    <i className={v === 'grid' ? 'ph-squares-four' : 'ph-list'} />
                  </button>
                ))}
              </div>
              <Button variant="light" icon="ph-arrows-clockwise" disabled={loading} onClick={() => load(activeOnly)}>
                Refresh
              </Button>
            </div>
            <div className="card-body">
              {loading ? (
                <div className="text-center py-5">
                  <Spinner />
                </div>
              ) : view === 'grid' ? (
                <ListingGrid
                  views={views}
                  now={now}
                  onOpen={openLot}
                  emptyMessage={activeOnly ? 'No active lots you’ve bid on.' : 'No bids yet.'}
                  surchargePercent={status?.surchargePercent ?? 0}
                />
              ) : (
                <DataTable
                  columns={columns}
                  rows={views}
                  getRowKey={(v) => String(v.id)}
                  onRowClick={(v) => openLot(v.id)}
                  pageSize={status?.favouritesPageSize ?? 0}
                  initialSort={{ key: 'closeTime', dir: 'desc' }}
                  emptyMessage={activeOnly ? 'No active lots you’ve bid on.' : 'No bids yet.'}
                />
              )}
            </div>
          </div>
        )}
      </div>
    </AppChrome>
  );
}
